/** Safe adapter for the verified fields of services/dashboards.js getDashboardCount.
 * Existing 'clients' and 'providers' count ACTIVE accounts CREATED in the date range.
 * They are NOT total clients or live/approved providers.
 * Existing 'totalRevenue' sums qualifying booking grand totals, NOT Beauty Spot fees.
 */
export const EMPTY = '—';
export const OVERVIEW_METRICS = Object.freeze([
  { key: 'newClients', label: 'New clients', definition: 'Active client accounts created in the selected period', source: 'clients' },
  { key: 'newProviders', label: 'New providers', definition: 'Active provider accounts created in the selected period; not necessarily live', source: 'providers' },
  { key: 'liveProviders', label: 'Live providers', definition: 'Approved, active and client-visible provider profiles', source: null },
  { key: 'confirmedBookings', label: 'Confirmed bookings', definition: 'Non-cancelled confirmed appointments', source: null },
  { key: 'completedBookings', label: 'Completed appointments', definition: 'Bookings marked finished after appointment completion', source: null },
  { key: 'awaitingCompletion', label: 'Awaiting completion', definition: 'Past-end-time bookings awaiting completion', source: null },
  { key: 'beautySpotFees', label: 'Beauty Spot fees', definition: 'Reconciled platform fees, net of applicable refunds', source: null },
]);

export function mapExistingDashboardCounts(payload) {
  // Existing endpoint uses res.data(...) and response envelopes vary by route.
  const data = payload?.data?.data ?? payload?.data ?? payload;
  const result = {};
  for (const metric of OVERVIEW_METRICS) {
    const raw = metric.source ? data?.[metric.source] : undefined;
    const value = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN);
    result[metric.key] = metric.source && Number.isFinite(value) && value >= 0
      ? { value, status: 'existing-api', definition: metric.definition }
      : { value: null, status: 'requires-backend', definition: metric.definition };
  }
  return result;
}


/** Contract for the dedicated Overview API once implemented on staging.
 * No guessed booking/revenue fields are accepted from the legacy endpoint.
 */
export function mapVerifiedOverview(payload) {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (!data || data.schemaVersion !== 1 || data.verified !== true) {
    throw new Error('The staging Overview API did not return a verified v1 response.');
  }
  const result = {};
  for (const metric of OVERVIEW_METRICS) {
    const value = data.metrics?.[metric.key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      result[metric.key] = { value: null, status: 'unavailable', definition: metric.definition };
    } else {
      result[metric.key] = { value, status: 'verified-api', definition: metric.definition };
    }
  }
  return {
    metrics: result,
    period: data.period,
    dateFrom: data.dateFrom,
    dateTo: data.dateTo,
    categories: Array.isArray(data.categories) ? data.categories.filter(x =>
      typeof x?.name === 'string' && Number.isInteger(x?.count) && x.count >= 0
    ) : [],
    bookingTrend: Array.isArray(data.bookingTrend) ? data.bookingTrend.filter(x =>
      typeof x?.date === 'string' && Number.isInteger(x?.count) && x.count >= 0
    ) : [],
    clientActivity: data.clientActivity && ["signups","converted","notBooked","conversionPct","returning"].every(k=>Number.isFinite(data.clientActivity[k])&&data.clientActivity[k]>=0) ? data.clientActivity : null,
    onboarding: data.onboarding && ["accountsCreated","setupIncomplete","setupCompleted","approvedLive","awaitingApproval"].every(k => Number.isInteger(data.onboarding[k]) && data.onboarding[k]>=0) ? data.onboarding : null,
    limitations: Array.isArray(data.limitations) ? data.limitations.filter(x => typeof x === 'string') : [],
    providersAwaitingApproval: Number.isInteger(data.providersAwaitingApproval) && data.providersAwaitingApproval >= 0
      ? data.providersAwaitingApproval : null,
  };
}
